import { useCallback, useRef, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Button, Container, Grid, IconButton, Paper, TextField, Typography } from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';

import useApp from '../../hooks/useApp';
import useTeam from '../../hooks/useTeam';
import { getErrorMessage } from '../../lib/error';
import { getSDKClient } from '../../lib/sdk';

const createInviteErrorDetailsToDisplayedMessage: Record<string, string> = {
  'invalid invitee email': 'Invalid email.',
  'invitee is already a member of the team': 'This email is already on the team.',
  'unable to create team invite': 'Unable to send this invitation. Please try again.',
};

export default function InviteTeamMember(): JSX.Element {
  const { app } = useApp();
  const { team } = useTeam();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [email, setEmail] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const isSendingRef = useRef(false);

  const inviteTeamMember = useCallback(
    async (email: string) => {
      if (email === '') {
        enqueueSnackbar('Empty email provided', { variant: 'error' });
        return;
      }
      if (team.team_id === '') {
        return;
      }
      if (isSendingRef.current) {
        return;
      }

      const { env, tokens } = app;
      const currentToken = tokens.get(env) ?? '';
      const sdk = getSDKClient(env, currentToken);
      isSendingRef.current = true;
      setIsSending(true);
      try {
        await sdk.createTeamInvite(team.team_id, email);
        enqueueSnackbar(`Invitation sent to ${email}`, { variant: 'success' });
        setEmail('');
      } catch (err) {
        let message = getErrorMessage(err);
        if (axios.isAxiosError(err)) {
          const details = err.response?.data?.error?.details;
          if (typeof details === 'string') {
            const mapped = createInviteErrorDetailsToDisplayedMessage[details];
            if (typeof mapped === 'string') {
              message = mapped;
            }
          }
        }
        enqueueSnackbar(message, { variant: 'error' });
      } finally {
        isSendingRef.current = false;
        setIsSending(false);
      }
    },
    [app, team, enqueueSnackbar],
  );

  return (
    <Container maxWidth="lg" sx={{ flexGrow: 1, p: 4 }}>
      <form
        onSubmit={(e) => {
          // prevent form from needlessly refreshing page
          e.preventDefault();
          inviteTeamMember(email);
        }}
      >
        <Grid
          container
          spacing={3}
          direction="row"
          alignItems="start"
          justifyContent="start"
          sx={{ display: { xs: 'block', sm: 'flex' } }}
        >
          <Grid item>
            <IconButton onClick={() => history.goBack()}>
              <ArrowBackIcon />
            </IconButton>
          </Grid>
          <Grid item flex={1}>
            <Grid container spacing={3} direction="column" display="flex">
              <Grid item>
                <Typography variant="h1" component="h1" gutterBottom>
                  Invite Member
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="h5" component="h5" fontWeight="bold">
                  Send an invitation to join {team.team_name}
                </Typography>
              </Grid>
              <Grid item>
                <Paper sx={{ p: 4 }}>
                  <TextField
                    id="email"
                    variant="outlined"
                    type="email"
                    fullWidth
                    label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    helperText="An invitation link will be sent to this address."
                    autoComplete="off"
                    disabled={isSending || team.team_id === ''}
                  />
                </Paper>
              </Grid>
              <Grid item justifyContent="center" display="flex">
                <Button variant="contained" size="large" type="submit" disabled={isSending || team.team_id === ''}>
                  Send Invitation
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
}
